import { withNamespaces } from "react-i18next";
import { MapContainer, TileLayer, useMap, FeatureGroup, GeoJSON } from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import L from "leaflet";
import { useEffect, useMemo, useRef, useState } from "react";
import { GeoSearchControl, OpenStreetMapProvider } from "leaflet-geosearch";
import * as turf from "@turf/turf";
import "leaflet/dist/leaflet.css";
import "leaflet-geosearch/dist/geosearch.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "./style.scss";
import OutofBoundaryModal from "./components/OutofBoundaryModal";
import { Spinner, UncontrolledTooltip } from "reactstrap";
import PlotValidationFailModal from "./components/PlotValidationFailModal";
import { RiZoomInLine } from "react-icons/ri";

const SearchField = ({ defaultSearchParam, readOnly }) => {
    const map = useMap();
    const provider = new OpenStreetMapProvider();

    useEffect(() => {
        if (!readOnly) {
            const searchControl = new GeoSearchControl({
                provider,
                style: "bar",
                autoComplete: true,
                keepResult: true,
            });

            map.addControl(searchControl);
        
            return () => map.removeControl(searchControl);
        }
    }, [ map, readOnly ]);

    useEffect(() => {
        if(defaultSearchParam && provider) {
            provider.search({ query: defaultSearchParam }).then((results) => {
                if (results && results.length > 0) {
                    const { x, y } = results[0];
                    map.setView([y, x], 6);
                }
            });        
        }
    }, [ 
        defaultSearchParam 
    ]);

    return null;
};

const ZoomToShapesButton = ({ t, featureGroupRef }) => {
    const map = useMap();

    const zoomToAll = () => {
        const fg = featureGroupRef.current;
        if (!fg) return;

        const bounds = fg.getBounds();
        if (bounds.isValid()) {
            map.fitBounds(bounds, { padding: [30, 30], maxZoom: 50 });
        }
    };

    return (
        <button
            type="button"
            className="btn btn-info"
            id="zoom-to-shapes-btn"
            onClick={zoomToAll}
            style={{
                position: 'absolute',
                bottom: '12px',
                right: '12px',
                zIndex: 1000
            }}
        >
            <RiZoomInLine size={20} className="me-1" />
            {t('Zoom to Shapes')}
            
            <UncontrolledTooltip
                target="zoom-to-shapes-btn"
            >
                {t('Zoom the map to fit all drawn shapes')}
            </UncontrolledTooltip>
        </button>
    );
}

const ZoomToGeoJSON = ({ geoData }) => {
    const map = useMap();

    useEffect(() => {
        const firstGeo = (Array.isArray(geoData) && geoData.length > 0) ? 
            geoData[0] : 
            (
                geoData?.features[0] ? geoData?.features[0] : geoData
            );

        if (firstGeo?.geometry) {
            try {
                const layer = L.geoJSON(firstGeo);
                const bounds = layer.getBounds();
                if (bounds.isValid()) {
                    map.fitBounds(bounds, { padding: [20, 20] });
                }
            } catch (err) {
                console.warn("Could not fit map to GeoJSON:", err);
            }
        }
    }, [geoData, map]);

    return null;
};

const GeoMap = ({ 
    t, 
    name,
    onAreaChange,
    onLoaded, 
    preDefinedGeo, 
    styles = {},
    readOnly = false,
    multiDraw = false,
    defaultSearchParam,
    isLoading = false,
    boundaryRestriction = {},
    plotValidation = {},
    displayZoomToShapesBtn = true
}) => {
    const [area, setArea] = useState([]);
    const [outofBoundaryModal, setShowOutofBoundaryModal] = useState(false);
    const [showValidationFailedModal, setShowValidationFailedModal] = useState(false);
    const featureGroupRef = useRef(null);
    const editedItemsRef = useRef(new Map());
    const editValidationTimeout = useRef(null);

    const getFeatureGroupInstance = () => {
        const fg = featureGroupRef.current;
        if (!fg) return null;
        return fg.leafletElement ?? fg;
    };

    const setElementTooltip = (layer, values) => {
        layer.unbindTooltip().bindTooltip(`${values.m2} m² (${values.ha} ha)`, {
            permanent: true,
            direction: "center",
            className: "polygon-label",
        }).openTooltip();
    };

    const calcAndSetArea = (layer) => {
        try {
            const geojson = layer.toGeoJSON();
            if(geojson.geometry?.type !== 'Polygon') return;
            
            const polygon = turf.polygon(geojson.geometry.coordinates);

            const areaM2 = turf.area(polygon);

            const areaHa = areaM2 / 10000;

            const areaToSet = {
                m2: areaM2.toFixed(2), 
                ha: areaHa.toFixed(2)
            }

            setElementTooltip(layer, areaToSet);

            setArea((currentState) => {
                const foundIndex = currentState.findIndex((ar) => ar.id === layer._leaflet_id);

                const newState = [...currentState];

                if(foundIndex < 0) {
                    newState.push({
                        id: layer._leaflet_id,
                        area: areaToSet
                    });
                }
                else{
                    newState[foundIndex].area = areaToSet
                }
                
                return newState;
            });
        } 
        catch(err) {
            console.error("Failed to calculate area:", err);
        }
    }   

    const applyInvalidStyleToLayer = (layer) => {
        layer.setStyle({
            color: "#dc3545",
            weight: 2,
            dashArray: "6,4",
            fillColor: "#f8d7da",
            fillOpacity: 0.5
        });

        layer.unbindTooltip().bindTooltip(`
            <div style="color:#dc3545; font-weight:500;">
                <span style="font-weight: bold;">${t('Invalid Plot')}</span><br />
                ${t('This plot failed plausibility validation.')}<br />
                ${t('Please redraw within valid boundaries.')}
            </div>
        `, {
            permanent: true,
            direction: "center",
            className: "polygon-label",
        }).openTooltip();
    }

    const applyValidatingStyleToLayer = (layer) => {
        layer.unbindTooltip().bindTooltip(`
                <div style="font-weight:500;">
                    <div role="status" class="me-1 spinner-border-sm spinner-border text-primary">
                        <span class="sr-only">${t('Loading')}...</span>
                    </div>
                    ${t('Validating')}...
                </div>
            `, {
            permanent: true,
            direction: "center",
            className: "polygon-label",
        }).openTooltip();
    }

    const handleCreated = async (e) => {
        const { layer } = e;

        const fgInstance = getFeatureGroupInstance();

        if (!fgInstance) {
            console.warn("FeatureGroup instance not available yet");
            return;
        }

        if (!!boundaryRestriction?.enabled) {
            const drawnGeoJSON = layer.toGeoJSON();

            const inside = turf.booleanWithin(
                drawnGeoJSON,
                boundaryRestriction.area
            );

            if (!inside) {
                setShowOutofBoundaryModal(true);

                try {
                    fgInstance.removeLayer(layer);
                } catch (err) {
                    console.warn("Could not clear existing layers:", err);
                }
                return;
            }
        }

        if (plotValidation?.enabled) {
            try{
                applyValidatingStyleToLayer(layer);

                await plotValidation.validate({
                    geoJSON: layer.toGeoJSON()
                });
            }
            catch (error) {
                try {
                    setShowValidationFailedModal(true);
                    applyInvalidStyleToLayer(layer);
                } catch (err) {
                    console.warn("Could not clear existing layers:", err);
                }
                return;
            }
        }

        if (!multiDraw) {
            if (typeof fgInstance.clearLayers === "function") {
                fgInstance.clearLayers();
            } 
            else {
                try {
                    fgInstance.eachLayer((l) => fgInstance.removeLayer(l));
                } catch (err) {
                    console.warn("Could not clear existing layers:", err);
                }
            }
        }

        if (typeof fgInstance.addLayer === "function") {
            fgInstance.addLayer(layer);
        } 
        else {
            console.warn("FeatureGroup.addLayer() not available", fgInstance);
        }

        calcAndSetArea(layer);

        onAreaChange && onAreaChange({
            name,
            action: 'create',
            id: layer._leaflet_id,
            geoJSON: layer.toGeoJSON()
        });
    };

    const validateEditedPlots = () => {
        if (!editedItemsRef.current || !editedItemsRef.current.size) return;

        const fgInstance = getFeatureGroupInstance();

        editedItemsRef.current.forEach(async (layer, leaflet_id) => {
            const drawnGeoJSON = layer.toGeoJSON();

            if (!!boundaryRestriction?.enabled) {
                const inside = turf.booleanWithin(
                    drawnGeoJSON,
                    boundaryRestriction.area
                );

                if (!inside) {
                    setShowOutofBoundaryModal(true);

                    try {
                        setArea((currentState) => {
                            return currentState.filter((ar) => ar.id !== leaflet_id);
                        });

                        fgInstance.removeLayer(layer);
                    } catch (err) {
                        console.warn("Could not clear existing layers:", err);
                    }

                    editedItemsRef.current.delete(leaflet_id)
                    return;
                }
            }

            if (plotValidation?.enabled) {
                try {
                    applyValidatingStyleToLayer(layer);

                    await plotValidation.validate({
                        geoJSON: drawnGeoJSON
                    });
                }
                catch (error) {
                    try {
                        setShowValidationFailedModal(true);
                        applyInvalidStyleToLayer(layer);
                    } catch (err) {
                        console.warn("Could not clear existing layers:", err);
                    }
                    editedItemsRef.current.delete(leaflet_id)
                    
                    return;
                }
            }

            calcAndSetArea(layer);
            onAreaChange && onAreaChange({
                name,
                action: 'edit',
                id: leaflet_id,
                geoJSON: drawnGeoJSON
            });

            editedItemsRef.current.delete(leaflet_id)
        });
    }

    const handleEdited = (e) => {
        if(editValidationTimeout.current) clearTimeout(editValidationTimeout.current);

        editValidationTimeout.current = setTimeout(() => {
            validateEditedPlots();
        }, 250);

        e.layers.eachLayer(async (layer) => {
            editedItemsRef.current.set(layer._leaflet_id, layer);
        });
    };

    const handleDeleted = (e) => {
        e.layers.eachLayer((layer) => {
            onAreaChange && onAreaChange({
                name,
                action: 'delete',
                id: layer._leaflet_id,
                geoJSON: layer.toGeoJSON()
            });

            setArea((currentState) => {
                return currentState.filter((ar) => ar.id !== layer._leaflet_id);
            });
        });
    };

    const prevValue = useMemo(() => {
        if (preDefinedGeo) {
            if (Array.isArray(preDefinedGeo)) {
                if (preDefinedGeo.length > 0) {
                    return preDefinedGeo;
                }
                else {
                    return null;
                }
            }

            return preDefinedGeo;
        }

        return null;
    }, [ preDefinedGeo ])

    useEffect(() => {
        setTimeout(() => {
            const fgInstance = getFeatureGroupInstance();

            if (prevValue && fgInstance) {
                const layer = L.geoJSON(prevValue);
                const appendedLayers = [];

                layer.eachLayer((l) => {
                    fgInstance.addLayer(l);

                    appendedLayers.push({
                        id: l._leaflet_id,
                        geoJSON: l.toGeoJSON()
                    })

                    calcAndSetArea(l);
                });

                onLoaded && onLoaded(appendedLayers, name);
            }
        }, 500);
    }, [ 
        prevValue,
        onLoaded
    ]);

    return (
        <div className="d-relative">
            <MapContainer
                className="map"
                center={[0, 0]}
                doubleClickZoom={false}
                zoom={4}
                style={styles}
            >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                {displayZoomToShapesBtn ? (
                    <ZoomToShapesButton 
                        featureGroupRef={featureGroupRef} 
                        t={t}
                    />
                ) : null}
                
                <FeatureGroup ref={featureGroupRef}>
                    {prevValue && (
                        <ZoomToGeoJSON geoData={prevValue} />
                    )}

                    <EditControl
                        position="topright"
                        draw={{
                            rectangle: !readOnly,
                            polygon: !readOnly,
                            polyline: false,
                            circle: false,
                            marker: false,
                            circlemarker: false
                        }}
                        edit={{
                            edit: !readOnly,
                            remove: !readOnly
                        }}
                        onCreated={handleCreated}
                        onEdited={handleEdited}
                        onDeleted={handleDeleted}
                    />
                </FeatureGroup>

                {!!boundaryRestriction?.enabled && (
                    <FeatureGroup>
                        <GeoJSON
                            data={boundaryRestriction.area}
                            style={{ color: "#5e4000", weight: 2, fillOpacity: 0.1 }}
                        />
                    </FeatureGroup>
                )}

                <SearchField
                    defaultSearchParam={prevValue ? null : defaultSearchParam}
                    readOnly={readOnly}
                />

                {isLoading ? (
                    <div className="leaflet-loading-overlay">
                        <div className="d-flex justify-content-center align-items-center">
                            <Spinner color="primary" size="sm" className="me-2" />
                            {t('Loading')}...
                        </div>
                    </div>
                ) : null}
            </MapContainer>

            {area?.length > 0 && (
                <div className="mt-2">
                    {area.map((ar, index) => {
                        return (
                            <p className="mb-1">
                                <strong>{`${t('Area')} ${index+1}`}:</strong> {ar.area.m2} m² ({ar.area.ha} ha)
                            </p>
                        )
                    })}
                </div>
            )}

            <OutofBoundaryModal 
                isOpen={outofBoundaryModal}
                toggle={() => setShowOutofBoundaryModal(false)}
                {...(boundaryRestriction?.errorMessage || {})}
            />

            <PlotValidationFailModal 
                isOpen={showValidationFailedModal}
                toggle={() => setShowValidationFailedModal(false)}
                {...(plotValidation?.errorMessage || {})}
            />
        </div>
    )
}

export default withNamespaces()(GeoMap);